import Stats from 'three/examples/jsm/libs/stats.module';
import { Vector3 } from 'three';
import { global } from '../../core/GlobalState';
import { diagnosticsService } from '../system/DiagnosticsService';
import { liveStreamRegistry } from '../networking/LiveStreamRegistry';
export class HudManager {
    constructor(config = {}) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        this.textElements = new Map();
        this.frameCount = 0;
        this.lastFrameTime = performance.now();
        this.fps = 0;
        this.lastPublishedFps = -1;
        this.diagnostics = diagnosticsService;
        this.prevUpdateTimestamp = performance.now();
        this.cpuAccumulator = 0;
        this.cpuSamples = 0;
        this.cpuLastComputed = performance.now();
        this.lastCpuText = '';
        this.lastMemoryText = '';
        this.lastStorageText = '';
        this.lastSystemSummary = '';
        this.lastSystemGpu = '';
        this.lastSystemUa = '';
        this.lastCameraText = '';
        this.lastNetworkText = '';
        this.lastConnectionsText = '';
        this.connectionsDirty = true;
        this.storageRefreshInterval = 20000;
        this.storageRefreshHandle = null;
        this.cameraEpsilonSq = 0.01 * 0.01;
        this.lastCameraPosition = new Vector3(Number.NaN, Number.NaN, Number.NaN);
        this.config = config;
        this.stats = new Stats();
        const panelMap = { fps: 0, ms: 1, mem: 2 };
        if (config.statsPanel) {
            const panelIndex = panelMap[config.statsPanel];
            this.stats.showPanel(panelIndex);
        }
        // Stats.js container (top-right, avoids overlap with custom HUD)
        const statsContainer = document.createElement('div');
        statsContainer.id = 'stats-container';
        statsContainer.appendChild(this.stats.dom);
        document.body.appendChild(statsContainer);
        this.stats.dom.style.display = config.statsPanel ? 'block' : 'none';
        // ✅ Restore automatic HUD element creation:
        const showCustomFps = (_a = config.customFPS) !== null && _a !== void 0 ? _a : false;
        const showCpu = (_b = config.cpuLoad) !== null && _b !== void 0 ? _b : false;
        const showMemory = (_c = config.memoryUsage) !== null && _c !== void 0 ? _c : false;
        const showStorage = (_d = config.storageUsage) !== null && _d !== void 0 ? _d : false;
        const showNetwork = false; //config.networkActivity ?? true;
        const showConnections = (_e = config.connections) !== null && _e !== void 0 ? _e : true;
        const showCamera = (_f = config.cameraPosition) !== null && _f !== void 0 ? _f : true;
        const showSystemSummary = false; //config.systemSummary ?? false;
        const showSystemGPU = false; //config.systemGPU ?? false;
        const showSystemUA = false; //config.systemUA ?? false;
        const showObjectCount = (_g = config.objectCount) !== null && _g !== void 0 ? _g : false;
        const showOrbCount = (_h = config.orbCount) !== null && _h !== void 0 ? _h : false;
        const showCurrentWorld = (_j = config.currentWorld) !== null && _j !== void 0 ? _j : true;
        const showLastOrbType = (_k = config.lastOrbType) !== null && _k !== void 0 ? _k : true;
        const showWorldsLoaded = (_l = config.worldsLoaded) !== null && _l !== void 0 ? _l : true;
        const showOrbRegistryCount = (_m = config.orbRegistryCount) !== null && _m !== void 0 ? _m : true;
        const showSemanticContext = (_o = config.semanticContext) !== null && _o !== void 0 ? _o : true;
        const showOrbSetInversionState = (_p = config.orbSetInversionState) !== null && _p !== void 0 ? _p : true;
        if (showConnections)
            this.addTextElement('connections', 'Connections');
        if (showCustomFps)
            this.addTextElement('fps', 'FPS');
        if (showCpu)
            this.addTextElement('cpuLoad', 'CPU Load');
        if (showMemory)
            this.addTextElement('memoryUsage', 'JS Heap');
        if (showStorage)
            this.addTextElement('storageUsage', 'Storage');
        if (showNetwork)
            this.addTextElement('networkActivity', 'Network');
        if (showCamera)
            this.addTextElement('cameraPosition', 'Camera');
        if (showSystemSummary)
            this.addTextElement('systemSummary', 'System');
        if (showSystemGPU)
            this.addTextElement('systemGPU', 'GPU');
        if (showSystemUA)
            this.addTextElement('systemUA', 'UA');
        if (showObjectCount)
            this.addTextElement('objectCount', 'Objects');
        if (showOrbCount)
            this.addTextElement('orbCount', 'Orbs');
        if (showCurrentWorld)
            this.addTextElement('currentWorld', 'World');
        if (showLastOrbType)
            this.addTextElement('lastOrbType', 'Last Orb');
        if (showWorldsLoaded)
            this.addTextElement('worldsLoaded', 'Worlds Loaded');
        if (showOrbRegistryCount)
            this.addTextElement('orbRegistryCount', 'Orb Registries');
        if (showSemanticContext)
            this.addTextElement('semanticContext', 'Semantic Context');
        if (showOrbSetInversionState)
            this.addTextElement('orbSetInversionState', 'Orb Setwise View');
        if (showStorage) {
            this.scheduleStorageRefresh(true);
        }
        if (showSystemSummary || showSystemGPU || showSystemUA) {
            this.updateSystemDiagnostics(true);
        }
        liveStreamRegistry.subscribe(() => {
            this.connectionsDirty = true;
        });
    }
    /**
     * Add a general text HUD element
     */
    addTextElement(id, label) {
        if (this.textElements.has(id))
            return;
        const div = document.createElement('div');
        div.id = `hud-${id}`;
        div.className = 'hud-text';
        div.textContent = `${label}: --`;
        const overlay = document.getElementById('overlay');
        if (overlay)
            overlay.appendChild(div);
        else
            document.body.appendChild(div);
        // Layout: stack vertically
        div.style.top = `${0.5 + this.textElements.size * 1.8}%`;
        this.textElements.set(id, {
            label,
            value: '--',
            dom: div
        });
    }
    /**
     * Update a HUD text element
     */
    updateTextElement(id, value) {
        const elem = this.textElements.get(id);
        if (!elem)
            return;
        const strValue = String(value);
        if (elem.value === strValue)
            return;
        elem.value = strValue;
        elem.dom.textContent = `${elem.label}: ${elem.value}`;
    }
    /**
     * Per-frame HUD update
     */
    update(scene) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        if (this.config.statsPanel) {
            this.stats.update();
        }
        if (this.config.customFPS) {
            this.updateCustomFPS();
        }
        if ((_a = this.config.cpuLoad) !== null && _a !== void 0 ? _a : true) {
            this.updateCpuLoad();
        }
        if ((_b = this.config.memoryUsage) !== null && _b !== void 0 ? _b : true) {
            this.updateMemoryUsage();
        }
        if ((_c = this.config.storageUsage) !== null && _c !== void 0 ? _c : true) {
            this.updateStorageUsage();
        }
        if ((_d = this.config.networkActivity) !== null && _d !== void 0 ? _d : true) {
            this.updateNetworkActivity();
        }
        if ((_e = this.config.cameraPosition) !== null && _e !== void 0 ? _e : true) {
            this.updateCameraPosition();
        }
        if (((_f = this.config.systemSummary) !== null && _f !== void 0 ? _f : true) || ((_g = this.config.systemGPU) !== null && _g !== void 0 ? _g : true) || ((_h = this.config.systemUA) !== null && _h !== void 0 ? _h : false)) {
            this.updateSystemDiagnostics();
        }
        if ((_j = this.config.connections) !== null && _j !== void 0 ? _j : true) {
            this.updateConnections();
        }
        if (this.config.objectCount) {
            this.updateTextElement('objectCount', scene.children.length);
        }
        if (this.config.orbCount) {
            this.updateTextElement('orbCount', global.orbManager.getAllOrbs().length);
        }
        if (((_k = this.config.currentWorld) !== null && _k !== void 0 ? _k : true) && global.hudDirty.currentWorld) {
            this.updateTextElement('currentWorld', global.hudCurrentWorld);
            global.hudDirty.currentWorld = false;
        }
        if (((_l = this.config.lastOrbType) !== null && _l !== void 0 ? _l : true) && global.hudDirty.lastSelectedOrbType) {
            this.updateTextElement('lastOrbType', global.hudLastSelectedOrbType || '—');
            global.hudDirty.lastSelectedOrbType = false;
        }
        if (((_m = this.config.worldsLoaded) !== null && _m !== void 0 ? _m : true) && global.hudDirty.worldsLoaded) {
            this.updateTextElement('worldsLoaded', global.hudWorldsLoaded);
            global.hudDirty.worldsLoaded = false;
        }
        if (((_o = this.config.orbRegistryCount) !== null && _o !== void 0 ? _o : true) && global.hudDirty.orbRegistryCount) {
            this.updateTextElement('orbRegistryCount', global.hudOrbRegistryCount);
            global.hudDirty.orbRegistryCount = false;
        }
        if (((_p = this.config.semanticContext) !== null && _p !== void 0 ? _p : true) && global.hudDirty.semanticContext) {
            const value = global.hudSemanticContext.length > 0
                ? global.hudSemanticContext.join(', ')
                : '—';
            this.updateTextElement('semanticContext', value);
            global.hudDirty.semanticContext = false;
        }
        if (((_q = this.config.orbSetInversionState) !== null && _q !== void 0 ? _q : true) && global.hudDirty.orbSetInversionState) {
            this.updateTextElement('orbSetInversionState', global.hudOrbSetInversionState);
            global.hudDirty.orbSetInversionState = false;
        }
    }
    /**
     * Manual FPS (text version)
     */
    updateCustomFPS() {
        const now = performance.now();
        this.frameCount++;
        if (now - this.lastFrameTime >= 1000) {
            this.fps = this.frameCount;
            this.frameCount = 0;
            this.lastFrameTime = now;
        }
        if (this.fps !== this.lastPublishedFps) {
            this.lastPublishedFps = this.fps;
            this.updateTextElement('fps', this.fps);
        }
    }
    updateCpuLoad() {
        const now = performance.now();
        let delta = now - this.prevUpdateTimestamp;
        this.prevUpdateTimestamp = now;
        if (!Number.isFinite(delta) || delta > 250) {
            delta = 250;
        }
        this.cpuAccumulator += delta;
        this.cpuSamples += 1;
        if (now - this.cpuLastComputed >= 1000 && this.cpuSamples > 0) {
            const avgFrame = this.cpuAccumulator / this.cpuSamples;
            const target = 1000 / 60;
            const load = Math.min(100, Math.max(0, (avgFrame / target) * 100));
            const text = `${Math.round(load)}%`;
            if (text !== this.lastCpuText) {
                this.lastCpuText = text;
                this.updateTextElement('cpuLoad', text);
            }
            this.cpuAccumulator = 0;
            this.cpuSamples = 0;
            this.cpuLastComputed = now;
        }
    }
    updateMemoryUsage() {
        const info = this.diagnostics.getMemoryInfo();
        let text = 'n/a';
        if (info) {
            const used = info.usedJSHeapSize / (1024 * 1024);
            const limit = info.jsHeapSizeLimit / (1024 * 1024);
            text = `${used.toFixed(1)} / ${limit.toFixed(1)} MB`;
        }
        if (text !== this.lastMemoryText) {
            this.lastMemoryText = text;
            this.updateTextElement('memoryUsage', text);
        }
    }
    updateStorageUsage() {
        const stats = this.diagnostics.getStorageStats();
        if (!stats) {
            if (this.lastStorageText !== 'n/a') {
                this.lastStorageText = 'n/a';
                this.updateTextElement('storageUsage', this.lastStorageText);
            }
            this.scheduleStorageRefresh(true);
            return;
        }
        const usageMb = stats.usage / (1024 * 1024);
        const quotaMb = stats.quota / (1024 * 1024);
        const percent = stats.quota > 0 ? (stats.usage / stats.quota) * 100 : 0;
        const persisted = stats.persisted === null ? '?' : stats.persisted ? 'Y' : 'N';
        const text = `${usageMb.toFixed(1)} / ${quotaMb.toFixed(1)} MB (${percent.toFixed(0)}%) P:${persisted}`;
        if (text !== this.lastStorageText) {
            this.lastStorageText = text;
            this.updateTextElement('storageUsage', text);
        }
        if (performance.now() - stats.timestamp > this.storageRefreshInterval) {
            this.scheduleStorageRefresh(true);
        }
    }
    formatCoords(position) {
        return `X:${position.x.toFixed(2)}  Y:${position.y.toFixed(2)}  Z:${position.z.toFixed(2)}`;
    }
    updateCameraPosition() {
        const camera = global.camera;
        if (!camera)
            return;
        const position = camera.position;
        // Initialize last known position
        if (!Number.isFinite(this.lastCameraPosition.x)) {
            this.lastCameraPosition.copy(position);
            this.lastCameraText = this.formatCoords(position);
            this.updateTextElement('cameraPosition', this.lastCameraText);
            return;
        }
        // Update only if camera moved significantly
        if (this.lastCameraPosition.distanceToSquared(position) > this.cameraEpsilonSq) {
            this.lastCameraPosition.copy(position);
            const text = this.formatCoords(position);
            if (text !== this.lastCameraText) {
                this.lastCameraText = text;
                this.updateTextElement('cameraPosition', text);
            }
        }
    }
    updateNetworkActivity() {
        const stats = this.diagnostics.getNetworkSnapshot();
        const mbps = stats.recentBytesPerSecond / (1024 * 1024);
        const text = `Act:${stats.activeRequests} Tot:${stats.completedRequests} | Req/s:${stats.recentRequestsPerSecond.toFixed(2)} | MB/s:${mbps.toFixed(2)} | Last:${(stats.lastTransferBytes / (1024 * 1024)).toFixed(2)}MB/${stats.lastDurationMs.toFixed(0)}ms`;
        if (text !== this.lastNetworkText) {
            this.lastNetworkText = text;
            this.updateTextElement('networkActivity', text);
        }
    }
    updateConnections() {
        if (!this.connectionsDirty) {
            return;
        }
        const snapshot = liveStreamRegistry.getSnapshot();
        const text = snapshot
            .map((stream) => `${stream.isConnected ? '🟢' : '🔴'} ${stream.label}`)
            .join(' | ');
        if (text !== this.lastConnectionsText) {
            this.lastConnectionsText = text;
            this.updateTextElement('connections', text || '—');
        }
        this.connectionsDirty = false;
    }
    updateSystemDiagnostics(force = false) {
        var _a, _b, _c;
        const system = this.diagnostics.getSystemDiagnostics();
        if ((_a = this.config.systemSummary) !== null && _a !== void 0 ? _a : true) {
            if (force || system.summary !== this.lastSystemSummary) {
                this.lastSystemSummary = system.summary;
                this.updateTextElement('systemSummary', system.summary);
            }
        }
        if ((_b = this.config.systemGPU) !== null && _b !== void 0 ? _b : true) {
            if (force || system.gpu !== this.lastSystemGpu) {
                this.lastSystemGpu = system.gpu;
                this.updateTextElement('systemGPU', system.gpu);
            }
        }
        if ((_c = this.config.systemUA) !== null && _c !== void 0 ? _c : false) {
            const uaPreview = system.userAgent.length > 140
                ? `${system.userAgent.slice(0, 137)}...`
                : system.userAgent;
            if (force || uaPreview !== this.lastSystemUa) {
                this.lastSystemUa = uaPreview;
                this.updateTextElement('systemUA', uaPreview);
            }
        }
    }
    scheduleStorageRefresh(force = false) {
        if (typeof window === 'undefined')
            return;
        if (force) {
            this.diagnostics.refreshStorageEstimate(true);
        }
        if (this.storageRefreshHandle === null) {
            this.storageRefreshHandle = window.setInterval(() => {
                this.diagnostics.refreshStorageEstimate();
            }, this.storageRefreshInterval);
        }
    }
    /**
     * Enable/disable stats panels dynamically
     */
    setStatsPanel(panel) {
        const panelMap = { fps: 0, ms: 1, mem: 2 };
        if (panel === 'none') {
            this.stats.dom.style.display = 'none';
        }
        else {
            this.stats.showPanel(panelMap[panel]);
            this.stats.dom.style.display = 'block';
        }
    }
    logStats() {
        console.log(`Custom FPS: ${this.fps}`);
    }
}
